import { Download, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TemplatePickerDialog } from '@/components/demand-template/TemplatePickerDialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useDownload } from '@/hooks/useDownload';
import { useGenerateDemandFromVirtualChatMutation } from '@/services/api/demandService';
import { selectMessagesByThread } from '@/services/slices/agentChat';
import { clearBusy } from '@/services/slices/demandGen';
import { RootState } from '@/state/state';

import {
  useCreateAgentChatAttachmentMutation,
  useDeleteAgentChatAttachmentMutation,
  useGetAgentChatOrchestratorStatusQuery,
  useListAgentChatMessagesQuery,
} from '../../services/api/agentChatService';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '../ui/resizable';
import { CitationViewer } from './citations/CitationViewer';
import { useAgentChat } from './context/AgentChatContext';
import { useAgentChatStream } from './hooks/useAgentChatStream';
import { MessageInput } from './messages/MessageInput';
import { MessageList } from './messages/MessageList';

interface AgentChatThreadProps {
  matterId: string;
  threadId: string;
  workspaceId?: string;
  onFirstMessage?: () => void;
  onToolCall?: (toolName: string, input: unknown, output?: unknown) => void;
  coverageId?: string | null;
  viewMode?: 'compact' | 'fullscreen';
}

export const AgentChatThread = ({
  matterId,
  threadId,
  workspaceId,
  onFirstMessage,
  onToolCall,
  coverageId,
  viewMode = 'compact',
}: AgentChatThreadProps) => {
  const { selectedCitation, setSelectedCitation } = useAgentChat();
  const [createAttachment] = useCreateAgentChatAttachmentMutation();
  const [deleteAttachment] = useDeleteAgentChatAttachmentMutation();

  // Per-thread streaming hook (handles auto-reconnect on mount)
  const {
    isInitializing,
    isSending,
    isStreaming,
    isReconnecting,
    restoredMessage,
    sendMessage,
    stopRun,
  } = useAgentChatStream(matterId, threadId);

  const { data: orchestratorStatus } = useGetAgentChatOrchestratorStatusQuery(
    {
      matterId,
      threadId,
    },
    { pollingInterval: 5_000, skipPollingIfUnfocused: true },
  );
  const { isLoading: isLoadingMessages } = useListAgentChatMessagesQuery({
    matterId,
    threadId,
  });

  const messages = useSelector((state: RootState) =>
    selectMessagesByThread(state.agentChat, threadId),
  );

  const dispatchBusy = useDispatch();

  useEffect(() => {
    if (orchestratorStatus && orchestratorStatus.status !== 'RUNNING') {
      dispatchBusy(clearBusy());
    }
  }, [orchestratorStatus, dispatchBusy]);

  // Track tool calls that have already been reported to avoid duplicates
  const reportedToolCallIds = useRef(new Set<string>());

  // Reset tracked tool calls when thread changes
  useEffect(() => {
    reportedToolCallIds.current.clear();
  }, [threadId]);

  // Detect completed tool calls and notify via onToolCall callback
  useEffect(() => {
    if (!onToolCall) return;

    // Build lookup of tool responses by tool_call_id
    const toolResponses = new Map<string, { content: unknown }>();
    messages
      .filter((msg) => msg.message.type === 'tool')
      .forEach((msg) => {
        if (msg.message.type === 'tool') {
          toolResponses.set(msg.message.tool_call_id, msg.message);
        }
      });

    // Scan AI messages for tool_calls that have matching responses
    messages
      .filter((msg) => msg.message.type === 'ai')
      .forEach((msg) => {
        if (msg.message.type !== 'ai') return;
        msg.message.tool_calls
          .filter((tc) => tc.id && !reportedToolCallIds.current.has(tc.id))
          .forEach((tc) => {
            const response = tc.id ? toolResponses.get(tc.id) : undefined;
            if (!response || !tc.id) return;
            reportedToolCallIds.current.add(tc.id);
            onToolCall(tc.name, tc.args, response.content);
          });
      });
  }, [messages, onToolCall]);

  const handleUploadAttachment = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('size', file.size.toString());

    if (file.type) {
      formData.append('content_type', file.type);
    }

    const attachment = await createAttachment({
      matterId,
      threadId,
      data: formData,
    }).unwrap();

    return attachment;
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    await deleteAttachment({
      matterId,
      threadId,
      attachmentId,
    }).unwrap();
  };

  const setSelectedCitationId = (index: string | null) => {
    if (index === null) {
      setSelectedCitation(null);
    } else if (selectedCitation) {
      setSelectedCitation({
        ...selectedCitation,
        selectedBlockId: index,
      });
    }
  };

  const handleCloseCitation = () => {
    setSelectedCitation(null);
  };

  const { downloadFile } = useDownload();

  const handleDownloadCitation = useCallback(() => {
    const url = selectedCitation?.materializedCitation?.url;
    if (!url) return;
    const rawName =
      selectedCitation?.materializedCitation?.name || 'document';
    const name = rawName.replace(/\.pdf$/i, '');
    downloadFile({ downloadUrl: url, name, fileExtension: 'pdf' });
  }, [selectedCitation, downloadFile]);

  // Demand generation state
  const [demandGenStatus, setDemandGenStatus] = useState<
    'idle' | 'running' | 'completed'
  >('idle');
  const [generateDemandFromVirtualChat] =
    useGenerateDemandFromVirtualChatMutation();

  // Modal state for choosing demand example
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);

  const handleTemplateConfirm = async (templateId: string) => {
    setIsSelectorOpen(false);
    try {
      setDemandGenStatus('running');
      await generateDemandFromVirtualChat({
        matterId,
        threadId,
        workspaceId,
        templateId,
      }).unwrap();
      // Start polling for status
      const pollStatus = async () => {
        const res = await fetch(
          `/api/matters/${matterId}/virtual-chats/${threadId}/status/`,
          { credentials: 'include' },
        );
        if (res.ok) {
          const json = await res.json();
          if (!json.is_running) {
            setDemandGenStatus('idle');
            return true;
          }
        }
        return false;
      };

      // Poll immediately and then every 3 seconds
      if (await pollStatus()) return;

      const intervalId = setInterval(async () => {
        if (await pollStatus()) {
          clearInterval(intervalId);
        }
      }, 3000);

      // Cleanup interval after 5 minutes (timeout)
      setTimeout(
        () => {
          clearInterval(intervalId);
          setDemandGenStatus('idle');
        },
        5 * 60 * 1000,
      );
    } catch (err) {
      setDemandGenStatus('idle');
    }
  };

  const showCitationPanel =
    viewMode === 'fullscreen' && selectedCitation !== null;

  return (
    <div className="relative h-full w-full z-40 bg-white">
      {/* Workspace busy overlay removed to keep thread interactive */}

      <ResizablePanelGroup direction="horizontal" className="h-full">
        <ResizablePanel defaultSize={showCitationPanel ? 60 : 100} minSize={40}>
          <div className="flex flex-col h-full">
            {isReconnecting && (
              <div className="relative overflow-hidden">
                {/* Shimmer bar */}
                <div className="h-[2px] w-full bg-gray-100 relative overflow-hidden">
                  <div
                    className="absolute inset-0 h-full w-1/2"
                    style={{
                      background: 'linear-gradient(90deg, transparent, #3e21f3, transparent)',
                      animation: 'reconnect-shimmer 1.8s ease-in-out infinite',
                    }}
                  />
                </div>
                {/* Content */}
                <div
                  className="flex items-center justify-center gap-2.5 py-2"
                  style={{ animation: 'reconnect-fade-in 0.3s ease-out' }}
                >
                  <div className="flex items-center gap-[3px]">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="block h-[5px] w-[5px] rounded-full"
                        style={{
                          backgroundColor: '#3e21f3',
                          animation: `reconnect-dot 1.4s ease-in-out ${i * 0.16}s infinite`,
                        }}
                      />
                    ))}
                  </div>
                  <span className="text-[11px] font-medium tracking-wide text-gray-400 uppercase">
                    Reconnecting
                  </span>
                </div>
              </div>
            )}
            {orchestratorStatus?.status === 'RUNNING' && (
              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 flex items-center space-x-2 mb-2">
                <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                <span className="text-blue-700 font-medium">
                  Task in progress...
                </span>
              </div>
            )}
            <MessageList
              messages={messages}
              isLoading={isLoadingMessages}
              matterId={matterId}
              threadId={threadId}
            />
            <div className="mb-4 mx-4">
              <MessageInput
                matterId={matterId}
                threadId={threadId}
                onUploadAttachment={handleUploadAttachment}
                onDeleteAttachment={handleDeleteAttachment}
                isDisabled={
                  isInitializing ||
                  isStreaming ||
                  orchestratorStatus?.status === 'RUNNING'
                }
                isSending={isSending}
                isStreaming={isStreaming}
                sendMessage={sendMessage}
                stopRun={stopRun}
                restoredMessage={restoredMessage}
                demandGenStatus={demandGenStatus}
                onFirstMessage={onFirstMessage}
              />
            </div>
          </div>
        </ResizablePanel>

        {/* Fullscreen: Citation Viewer Panel */}
        {showCitationPanel && (
          <>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={40} minSize={25} maxSize={60}>
              <div className="h-full relative">
                <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
                  <button
                    onClick={handleDownloadCitation}
                    className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    aria-label="Download document"
                    type="button"
                  >
                    <Download size={18} />
                  </button>
                  <button
                    onClick={handleCloseCitation}
                    className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    aria-label="Close citation viewer"
                    type="button"
                  >
                    <X size={18} />
                  </button>
                </div>
                <CitationViewer
                  url={selectedCitation?.materializedCitation?.url}
                  citations={
                    selectedCitation?.materializedCitation?.blocks || []
                  }
                  selectedCitationId={selectedCitation?.selectedBlockId}
                  setSelectedCitationId={setSelectedCitationId}
                  shouldRender
                />
              </div>
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>

      {/* Compact: Citation Viewer Modal */}
      {viewMode !== 'fullscreen' && (
        <Dialog
          open={!!selectedCitation}
          onOpenChange={(open) => !open && handleCloseCitation()}
        >
          <DialogContent className="max-w-4xl w-[90vw] h-[85vh] p-0 overflow-hidden z-1000" hideCloseButton>
            <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
              <button
                onClick={handleDownloadCitation}
                className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                aria-label="Download document"
                type="button"
              >
                <Download size={18} />
              </button>
              <button
                onClick={handleCloseCitation}
                className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                aria-label="Close citation viewer"
                type="button"
              >
                <X size={18} />
              </button>
            </div>
            {selectedCitation && (
              <CitationViewer
                url={selectedCitation?.materializedCitation?.url}
                citations={selectedCitation?.materializedCitation?.blocks || []}
                selectedCitationId={selectedCitation?.selectedBlockId}
                setSelectedCitationId={setSelectedCitationId}
                shouldRender
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      <TemplatePickerDialog
        open={isSelectorOpen}
        onOpenChange={setIsSelectorOpen}
        onConfirm={handleTemplateConfirm}
        confirmLabel="Run"
        matterId={matterId}
        workspaceId={workspaceId}
        currentThreadId={threadId}
      />
    </div>
  );
};
